/*
 * SupplierInvoiceForm.tsx : Un formulaire pour ajouter ou modifier une facture fournisseur.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Table } from '@radix-ui/themes';

// Import FontAwesome
import {
	faCoins,
	faFileInvoiceDollar,
	faIndustry,
	faInfoCircle,
	faReceipt,
	faUnlink,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs
import {
	DatePickerField,
	finishFormSave,
	FormSaveActions,
	InputField,
	SelectField,
	StatusCard,
	useFormDirty,
} from '../../../common';
import { useWorkflowStatusConfig } from '../../../common/hooks/useWorkflowStatusConfig';
import WorkflowLockBanner from '../../../common/components/WorkflowLockBanner/WorkflowLockBanner';

// Import Layout
import CardHeader from '../../../layout/CardHeader';
import CardSection from '../../../layout/CardSection';

// Import SupplierInvoice
import {
	SUPPLIER_INVOICE_STATUS_CONFIG,
	SupplierInvoiceData,
} from '../types/types';
import { supplierInvoicesApi } from '../services/supplierInvoicesApi';

// Import Tiers
import { tiersApi } from '../../tiers/services/tiersApi';

// Import Utilisateurs
import { usersApi } from '../../users/services/usersApi';
import { OrderLineExpenseAssociationCard } from 'features/orders/components/OrderLineExpenseAssociationCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { expensesApi } from 'features/expenses';

function editableInvoice(invoice: SupplierInvoiceData) {
	const editable = { ...invoice };
	delete editable.status;
	delete editable.workflowLock;
	return editable;
}

// Composant SupplierInvoiceForm
const SupplierInvoiceForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<SupplierInvoiceData>({
		id: 0,
		label: '',
		supplierInvoiceReference: null,
		supplierInvoiceDate: null,
		supplier: null,
		internalContact: null,
		amount: null,
		createdAt: new Date(),
		status: 'DRAFT',
		penaltyAndCarePercent: null,
		expenses: [],
		referenceNumber: null,
	});

	const [supplierOptions, setSupplierOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [internalContactOptions, setInternalContactOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const statusConfig = useWorkflowStatusConfig(
		'supplier_invoice',
		SUPPLIER_INVOICE_STATUS_CONFIG
	);

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	// L'état et le verrou sont enregistrés dès le clic sur le bloc État.
	const { isDirty, markClean } = useFormDirty(
		editableInvoice(formData),
		baselineReady
	);

	// Hook pour le prochain numéro de référence
	const [nextRef, setNextRef] = React.useState<string | null>(null);

	// Une fois le composant monté et si un id est présent, fetch des données et préremplissage du formulaire
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// On commence par charger les tiers (fournisseurs)
		tiersApi
			.listSelectSuppliers(token)
			.then((items) => {
				const options = items
					.filter((item) => item.id != null)
					.map((item) => ({
						key: item.id.toString(),
						label: String(item.label),
					}));
				setSupplierOptions(options);
			})
			.catch((error) => console.error(error));

		// puis les utilisateurs (interlocuteurs internes et créateurs)
		usersApi
			.listSelect(token)
			.then((items) => {
				const options = items
					.filter((item) => item.id != null)
					.map((item) => ({
						key: String(item.id),
						label: String(item.label),
					}));
				setInternalContactOptions(options);
			})
			.catch((error) => console.error(error));

		// Si un id est présent, fetch des données de la facture fournisseur à éditer
		if (id) {
			supplierInvoicesApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
		}
	}, [id]);

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion des changements dans le formulaire pour les selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'supplier') {
			const selectedOption = supplierOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					supplier: {
						id: Number(selectedOption.key),
						label: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'internalContact') {
			const selectedOption = internalContactOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					internalContact: {
						id: Number(selectedOption.key),
						name: selectedOption.label,
						firstName: '',
						login: '',
						email: '',
					},
				}));
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Lazy load du prochain numéro de référence
	React.useEffect(() => {
		// Vérification de la création d'une nouvelle commande
		if (id) return;

		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si on n'a pas déjà récupéré la prochaine référence
		if (!loading && nextRef === null) {
			// Récupération de l'année en cours
			const year = new Date().getFullYear();

			// Fetch du prochain numéro de référence
			supplierInvoicesApi
				.previewReference(token, year)
				.then((data) => {
					setNextRef(data.nextReference);
					// Mise à jour du numéro de référence dans le formulaire
					setFormData((prevData) => ({
						...prevData,
						referenceNumber: 'FF' + data.nextReference,
					}));
					setBaselineReady(true);
				})
				.catch((error) => {
					console.error(error);
					setBaselineReady(true);
				});
		}
	}, [loading]);

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	// Sauvegarde avec ou sans redirection
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/supplierInvoices');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			supplierInvoicesApi
				.update(Number(id), formData, token)
				.then((updated) => {
					if (updated) {
						setFormData(updated);
					}
					finishFormSave(shouldLeave, {
						onLeave,
						markClean: () => markClean(editableInvoice(updated ?? formData)),
					});
				})
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			supplierInvoicesApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/supplierInvoices/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	const handleStatusChange = (newStatus: string) => {
		if (!formData.id) return;
		const token = localStorage.getItem('token');
		if (!token) return;
		supplierInvoicesApi
			.update(formData.id, { status: newStatus }, token)
			.then((updated) => {
				setFormData((prev) => ({
					...prev,
					status: updated?.status ?? newStatus,
					workflowLock: updated?.workflowLock ?? null,
				}));
			})
			.catch((error) => console.error(error));
	};

	// Gestion de la dissociation d'une dépense liée à la facture fournisseur
	const onUnlink = (expense: { id: number }) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		expensesApi.get(Number(expense.id), token).then((expenseData) => {
			const updatedExpense = {
				...expenseData,
				supplierInvoice: null,
			};
			expensesApi
				.update(Number(expense.id), updatedExpense, token)
				.then(() => {
					// Mise à jour du formulaire en retirant la dépense dissociée
					setFormData((prev) => ({
						...prev,
						expenses: prev.expenses?.filter((e) => e.id !== expense.id),
					}));
				})
				.catch((error) => console.error(error));
		});
	};

	// Rendu du composant
	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-8xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-4">
				{/* HEADER DE LA CARD */}
				<CardHeader
					icon={faFileInvoiceDollar}
					title={
						id
							? "Édition d'une facture fournisseur"
							: 'Nouvelle facture fournisseur'
					}
					loading={loading}
					createdAt={formData.createdAt ? formData.createdAt.toString() : ''}
				/>

				{/* CONTENU DE LA CARD */}
				<div className="mt-3 w-full">
					<WorkflowLockBanner lock={formData.workflowLock} />
					<form
						onSubmit={handleSubmit}
						className="grid grid-cols-1 md:grid-cols-3 gap-6"
					>
						{/* COLONNE GAUCHE */}
						<CardSection
							icon={faInfoCircle}
							title="Informations générales"
							className="space-y-4"
						>
							<InputField
								label="Label"
								name="label"
								value={formData.label}
								onChange={handleChange}
								required
								loading={loading}
								placeholder='ex: "Facture électricité 2023"'
							/>
							<InputField
								label="Référence interne"
								name="referenceNumber"
								value={formData.referenceNumber || ''}
								onChange={handleChange}
								required
								loading={loading}
								placeholder="Chargement..."
								readOnly={id ? false : true}
							/>
							<SelectField
								label="Interlocuteur interne"
								selectedKey={formData.internalContact?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'internalContact',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={internalContactOptions}
								loading={loading}
								entityHref={
									formData.internalContact?.id
										? `/app/users/${formData.internalContact.id}/edit`
										: undefined
								}
								onClear={() =>
									setFormData((prev) => ({
										...prev,
										internalContact: null,
									}))
								}
							/>
						</CardSection>
						{/* COLONNE DU MILIEU */}
						<CardSection
							icon={faIndustry}
							title="Fournisseur"
							className="space-y-4"
						>
							<SelectField
								label="Fournisseur"
								selectedKey={formData.supplier?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'supplier',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={supplierOptions}
								loading={loading}
								entityHref={
									formData.supplier?.id
										? `/app/tiers/${formData.supplier.id}/edit`
										: undefined
								}
								onClear={() =>
									setFormData((prev) => ({
										...prev,
										supplier: null,
									}))
								}
							/>
							<InputField
								label="Référence fournisseur"
								name="supplierInvoiceReference"
								value={formData.supplierInvoiceReference || ''}
								onChange={handleChange}
								required
								loading={loading}
								placeholder='ex: "REF-2023_9167451"'
							/>
							<DatePickerField
								label="Date de la facture fournisseur"
								name="supplierInvoiceDate"
								value={
									formData.supplierInvoiceDate
										? new Date(formData.supplierInvoiceDate)
										: undefined
								}
								onChange={(date) =>
									setFormData({ ...formData, supplierInvoiceDate: date })
								}
								required
								loading={loading}
							/>
						</CardSection>
						{/* COLONNE DE DROITE */}
						<div className="flex flex-col gap-6">
							{id && (
								<StatusCard
									status={formData.status}
									config={statusConfig}
									onStatusChange={handleStatusChange}
									size="md"
									loading={loading}
									className="w-full"
								/>
							)}
							<CardSection icon={faCoins} title="Budget" className="space-y-4">
								<InputField
									label="Montant HT"
									name="amount"
									type="number"
									value={formData.amount || 0}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "1000.0"'
								/>
								<InputField
									label="Peine et soins (%)"
									name="penaltyAndCarePercent"
									type="number"
									value={formData.penaltyAndCarePercent || 0}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "5"'
								/>
							</CardSection>
						</div>
						{/* Deuxième ligne : une Section qui fusionne les trois colonnes */}
						<CardSection
							icon={faReceipt}
							title="Dépenses associées"
							className="md:col-span-3"
						>
							{/* Conteneur relatif pour ancrer la jauge en absolu au niveau du titre */}
							<div className="relative">
								{/* Jauge de progression dépenses / montant facture — positionnée en absolu en haut à droite */}
								{(() => {
									const totalDepenses = (formData.expenses ?? []).reduce(
										(sum, e) => sum + Number(e.amountHtInvoiced ?? 0),
										0
									);
									const montantFacture = Number(formData.amount ?? 0);
									const pct =
										montantFacture > 0
											? Math.round((totalDepenses / montantFacture) * 100)
											: null;
									// Couleur basée sur les montants € (pas le % arrondi)
									const barColor =
										montantFacture <= 0
											? 'bg-gray-300'
											: totalDepenses > montantFacture
												? 'bg-red-500'
												: totalDepenses < montantFacture
													? 'bg-orange-400'
													: 'bg-green-500';
									const textColor =
										montantFacture <= 0 || pct === null
											? 'text-gray-400'
											: totalDepenses > montantFacture
												? 'text-red-500'
												: totalDepenses < montantFacture
													? 'text-orange-500'
													: 'text-green-600';
									const fmtEur = (v: number) =>
										new Intl.NumberFormat('fr-FR', {
											style: 'currency',
											currency: 'EUR',
										}).format(v);
									return (
										<div className="absolute -top-10 right-0 w-1/3 flex flex-col gap-1">
											{/* Barre de progression avec fond gris visible */}
											<div className="h-2 bg-gray-200 rounded-full overflow-hidden">
												<div
													className={`h-2 rounded-full transition-all ${barColor}`}
													style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
												/>
											</div>
											{/* Légende texte */}
											<div className="flex justify-between text-xs text-gray-500">
												<span>
													{
														<span className={`font-semibold`}>
															{fmtEur(totalDepenses)}
														</span>
													}
													{' / '}
													{montantFacture > 0 ? fmtEur(montantFacture) : '—'}
												</span>
												{pct !== null && (
													<span className={`font-semibold ${textColor}`}>
														{pct}%
													</span>
												)}
											</div>
										</div>
									);
								})()}

								<div className="pt-2 pb-2">
									{/* Liste des dépenses associées */}
									{/* Si il n'y a pas de dépenses, on propose d'en ajouter, sinon on affiche le tableau */}
									{!formData?.expenses?.length ? (
										<p className="text-gray-500">Aucune dépense.</p>
									) : (
										<Table.Root variant="ghost" layout="auto">
											<Table.Header>
												<Table.Row>
													<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
														ID
													</Table.ColumnHeaderCell>
													<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
														Libellé
													</Table.ColumnHeaderCell>
													<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
														Type
													</Table.ColumnHeaderCell>
													<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
														Origine
													</Table.ColumnHeaderCell>
													<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
														Commande
													</Table.ColumnHeaderCell>
													<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
														Affaire
													</Table.ColumnHeaderCell>
													<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
														Date
													</Table.ColumnHeaderCell>
													<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
														Montant
													</Table.ColumnHeaderCell>
													<Table.ColumnHeaderCell className="text-gray-500 !font-normal text-right">
														Actions
													</Table.ColumnHeaderCell>
												</Table.Row>
											</Table.Header>
											<Table.Body>
												{formData?.expenses && formData?.expenses.length > 0 ? (
													formData?.expenses.map((expense) => (
														<Table.Row key={expense.id}>
															<Table.Cell>
																<a
																	href={`/app/expenses/${expense.id}/edit`}
																	className="text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
																>
																	{expense.id}
																</a>
															</Table.Cell>
															<Table.Cell>{expense.label}</Table.Cell>
															<Table.Cell>
																{/* mapping des types */}
																{expense.type === 'PROJECT'
																	? 'Affaire'
																	: expense.type === 'OPERATIONAL'
																		? 'Exploitation'
																		: expense.type === 'CAPITAL'
																			? 'Investissement'
																			: expense.type}
															</Table.Cell>
															<Table.Cell>
																{/* mapping des origines */}
																{expense.origin === 'ORDER'
																	? 'Commande'
																	: expense.origin === 'CONTRACT'
																		? 'Contrat'
																		: expense.origin === 'FREE'
																			? 'Libre'
																			: expense.origin}
															</Table.Cell>
															{/* Référence de la commande fournisseur liée (via orderLine) */}
															<Table.Cell>
																{expense.businessOrder ? (
																	<a
																		href={`/app/orders/${expense.businessOrder.id}/manage`}
																		className="text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
																	>
																		{expense.businessOrder.referenceNumber}
																	</a>
																) : (
																	'—'
																)}
															</Table.Cell>
															{/* Référence de l'affaire liée (si type = PROJECT) */}
															<Table.Cell>
																{expense.project ? (
																	<a
																		href={`/app/projects/${expense.project.id}/edit`}
																		className="text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
																	>
																		{expense.project.referenceNumber}
																	</a>
																) : (
																	'—'
																)}
															</Table.Cell>
															<Table.Cell>
																{expense.expenseDate
																	? new Date(
																			expense.expenseDate
																		).toLocaleDateString()
																	: ''}
															</Table.Cell>
															<Table.Cell>
																{expense.amountHtInvoiced != null
																	? new Intl.NumberFormat('fr-FR', {
																			style: 'currency',
																			currency: 'EUR',
																		}).format(expense.amountHtInvoiced)
																	: '-'}
															</Table.Cell>
															<Table.Cell className="text-gray-500 !font-normal text-right">
																<FontAwesomeIcon
																	icon={faUnlink}
																	className="ml-2 w-4 h-4 text-gray-300 hover:text-red-500 cursor-pointer"
																	onClick={() => onUnlink({ id: expense.id })}
																/>
															</Table.Cell>
														</Table.Row>
													))
												) : (
													<Table.Row>
														<Table.Cell
															colSpan={9}
															className="text-center text-gray-500"
														>
															Aucune dépense.
														</Table.Cell>
													</Table.Row>
												)}
											</Table.Body>

											{/* Ligne de total HT en pied de tableau */}
											<tfoot>
												{(() => {
													const totalDepenses = (
														formData.expenses ?? []
													).reduce(
														(sum, e) => sum + Number(e.amountHtInvoiced ?? 0),
														0
													);
													const fmtEur = (v: number) =>
														new Intl.NumberFormat('fr-FR', {
															style: 'currency',
															currency: 'EUR',
														}).format(v);
													return (
														<tr className="border-t border-gray-200">
															{/* Colonnes vides : ID, Libellé, Type, Origine, Commande, Affaire, Date */}
															<td colSpan={7} />
															<td className="text-gray-700 font-semibold text-sm py-2 px-3">
																{fmtEur(totalDepenses)}
															</td>
															<td />
														</tr>
													);
												})()}
											</tfoot>
										</Table.Root>
									)}
								</div>
							</div>{' '}
							{/* fin div.relative */}
						</CardSection>
						{loading ? (
							<span>Chargement des lignes de commandes...</span>
						) : (
							<div className="md:col-span-3">
								<OrderLineExpenseAssociationCard
									size="xs"
									invoiceId={formData.id}
									supplierId={formData.supplier?.id || undefined}
									className="!max-w-full"
								/>
							</div>
						)}
					</form>

					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => navigate('/app/supplierInvoices')}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default SupplierInvoiceForm;
